import React from 'react';
import { Loader2, Users, Lock } from 'lucide-react';
import { cn } from '@/lib/utils';
import ReplyStatusChip from './ReplyStatusChip';
import { KIND_PLACEHOLDER, fmtListTime } from '../../lib/chatsView';

/*
 * r16p (design 7.1): the conversation list. One row per conversation: who it is with (exactly what the server sends - a display
 * name or a username), the last activity, a short preview (cut by the server), an unread dot, a "needs reply" mark, the folder chip
 * (General / Requests), the slot (in the all-slots view) and the status chip of our last reply. An initial in a circle stands in
 * for the avatar: no remote images (they would leak the viewer's IP to Instagram).
 */
function Avatar({ initial, group }) {
  return (
    <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary/25 to-accent/25 text-sm font-semibold text-foreground ring-1 ring-white/10" aria-hidden>
      {group ? <Users className="h-4 w-4" /> : initial}
    </span>
  );
}

function previewLine(t) {
  const p = t.preview || {};
  const kindText = p.kind && p.kind !== 'text' ? KIND_PLACEHOLDER[p.kind] : null;
  const body = p.text || kindText || (p.label ? p.label : '');
  if (!body) return '';
  return p.dir === 'out' ? `You: ${body}` : body;
}

export default function ThreadList({ threads, selectedId, onOpen, showSlot, hasMore, onMore, loadingMore, now, emptyText }) {
  if (!threads.length) {
    return (
      <div className="px-4 py-8 text-center text-sm text-muted-foreground text-pretty" data-testid="thread-list-empty">
        {emptyText}
      </div>
    );
  }
  return (
    <div className="min-h-0 flex-1 overflow-y-auto" data-testid="thread-list">
      <ul className="divide-y divide-border/50">
        {threads.map((t) => {
          const unread = t.unread || t.dashUnread;
          const status = t.lastReply && t.lastReply.status;
          return (
            <li key={t.id}>
              <button
                type="button"
                onClick={() => onOpen(t.id)}
                className={cn(
                  'flex w-full min-w-0 items-start gap-3 px-3 py-3 text-left transition-colors sm:px-4',
                  t.id === selectedId ? 'bg-primary/12 ring-1 ring-inset ring-primary/25' : 'hover:bg-secondary/60',
                )}
                aria-current={t.id === selectedId ? 'true' : undefined}
                data-testid={`thread-row-${t.id}`}
                data-unread={unread ? 'true' : 'false'}
              >
                <Avatar initial={t.peer.initial} group={t.isGroup} />
                <span className="min-w-0 flex-1">
                  <span className="flex min-w-0 items-baseline gap-2">
                    <span className={cn('min-w-0 flex-1 truncate text-sm', unread ? 'font-semibold text-foreground' : 'font-medium text-foreground/90')}>
                      {t.peer.display}
                    </span>
                    <span className={cn('shrink-0 text-[11px] tnum', unread ? 'text-primary' : 'text-muted-foreground')}>
                      {fmtListTime(t.lastActivityAt, now)}
                    </span>
                  </span>
                  <span className="mt-0.5 flex min-w-0 items-center gap-2">
                    <span className={cn('min-w-0 flex-1 truncate text-xs', unread ? 'text-foreground/85' : 'text-muted-foreground')} data-testid={`thread-preview-${t.id}`}>
                      {previewLine(t)}
                    </span>
                    {unread && <span className="h-2 w-2 shrink-0 rounded-full bg-primary" title="Unread" aria-label="Unread" data-testid={`thread-unread-${t.id}`} />}
                  </span>
                  {(t.needsReply || status || (t.folder && t.folder !== 'primary') || showSlot || t.detached || t.neverOpen) && (
                    <span className="mt-1.5 flex flex-wrap items-center gap-1.5">
                      {showSlot && t.slot && (
                        <span className="rounded-full border border-border bg-muted/40 px-1.5 py-0.5 font-mono text-[10px] leading-none text-muted-foreground">{t.slot}</span>
                      )}
                      {t.needsReply && (
                        <span className="rounded-full border border-info/30 bg-info/10 px-1.5 py-0.5 text-[10px] font-medium leading-none text-info" data-testid={`thread-needs-${t.id}`}>
                          Needs reply
                        </span>
                      )}
                      {t.folder && t.folder !== 'primary' && (
                        <span className="rounded-full border border-border bg-muted/40 px-1.5 py-0.5 text-[10px] capitalize leading-none text-muted-foreground">{t.folder}</span>
                      )}
                      {t.detached && (
                        <span className="rounded-full border border-border bg-muted/40 px-1.5 py-0.5 text-[10px] leading-none text-muted-foreground">Not in the inbox now</span>
                      )}
                      {t.neverOpen && (
                        <span className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/40 px-1.5 py-0.5 text-[10px] leading-none text-muted-foreground">
                          <Lock className="h-2.5 w-2.5" aria-hidden />Not opened on the phone
                        </span>
                      )}
                      {status && <ReplyStatusChip status={status} compact testid={`thread-reply-status-${t.id}`} />}
                    </span>
                  )}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
      {hasMore && (
        <div className="p-3">
          <button type="button" onClick={onMore} disabled={loadingMore} data-testid="thread-list-more"
            className="tap inline-flex w-full items-center justify-center gap-2 rounded-xl border border-border/70 bg-secondary/40 px-3 py-2 text-sm text-foreground hover:bg-secondary/70 disabled:opacity-60">
            {loadingMore && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
            Load more conversations
          </button>
        </div>
      )}
    </div>
  );
}
